import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Amplify, I18n, Auth, Hub } from "aws-amplify"
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Button,
  CardFooter,
} from "reactstrap"
import Lottie from "lottie-react"

//redux
import { useSelector, useDispatch } from "react-redux"
import { withRouter, Link } from "react-router-dom"

// Formik validation
import * as Yup from "yup"
import { useFormik } from "formik"

// actions
import { loginUser, loginUserGloria } from "../../store/actions"

// import images
import profile from "assets/images/profile-img.png"

import awsConfig from "../../aws-exports"
import showToast from "../../components/Common/viewNotification"
import loadingAD from "../../assets/json/load-fragmented-square.json"
import Footer from "./footer"

Amplify.configure(awsConfig)
I18n.setLanguage("es")

const Login = props => {
  const [logo, setLogo] = useState()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  //meta title
  document.title = t("global:title")

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required(t("messagesLogin:email")),
      password: Yup.string().required(t("messagesLogin:password")),
    }),
    onSubmit: values => {
      try {
        dispatch(loginUser(values, props.history))
      } catch (e) {
        showToast(e.message, "error")
      }
    },
  })

  const { error, user, loading } = useSelector(state => ({
    error: state.Login.error,
    user: state.Login.user,
    loading: state.Login.loading,
  }))
  useEffect(() => {
    document.getElementById("body").className = "LoginPage"
    import(
      "assets/images/" + process.env.REACT_APP_STYLE_TEMPLATE + "/logo.svg"
    ).then(setLogo)
    import("assets/css/" + process.env.REACT_APP_STYLE_TEMPLATE + "/login.css")
  }, [])

  useEffect(() => {
    if (error) {
      showToast(error, "error")
    }
  }, [error])
  useEffect(() => {
    const errorSession = JSON.parse(sessionStorage.getItem("errorSession"))
    if (user) {
      if (!errorSession) {
        showToast(t("messagesLogin:loginSuccess"), "success")
        props.history.push("/")
      } else {
        resetLogin()
        showToast(errorSession.error, "warning")
      }
    }
  }, [user])

  useEffect(() => {
    Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signIn":
          showToast(t("messagesLogin:loginADSuccessAwait"), "success")
          break
        case "cognitoHostedUI":
          handleSubmit()
          break
        case "signOut":
          break
        case "signIn_failure":
          break
        case "cognitoHostedUI_failure":
          break
        default:
          break
      }
    })
  })

  const resetLogin = () => {
    setTimeout(() => {
      Auth.signOut()
    }, 200)
  }

  const handleSubmit = () => {
    try {
      dispatch(loginUserGloria(validation.values, history))
    } catch (e) {
      showToast(e.message, "error")
      resetLogin()
    }
  }

  return loading ? (
    <div>
      <Lottie animationData={loadingAD} />
    </div>
  ) : (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5 login-template">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary bg-soft">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">
                          {t("messagesLogin:title")}
                        </h5>
                        <p>{t("messagesLogin:subTitle")}</p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/" className="auth-logo-light">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-info">
                          <img
                            src={logo?.default}
                            alt=""
                            className="rounded-circle"
                            height="64"
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      {error ? <Alert color="danger">{error}</Alert> : null}

                      <div className="mb-3">
                        <Label className="form-label">
                          {t("authentication:account")}
                        </Label>
                        <Input
                          name="email"
                          className="form-control"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">
                          {t("authentication:password")}
                        </Label>
                        <Input
                          name="password"
                          value={validation.values.password || ""}
                          type="password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.password &&
                            validation.errors.password
                              ? true
                              : false
                          }
                        />
                        {validation.touched.password &&
                        validation.errors.password ? (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mt-3 d-grid">
                        <button
                          className="btn btn-primary btn-block"
                          type="submit"
                        >
                          <i className="mdi mdi-security me-1" />
                          {t("authentication:login")}
                        </button>
                      </div>

                      <div className="mt-4 text-center">
                        <Button
                          color="info"
                          outline
                          onClick={() =>
                            Auth.federatedSignIn({ provider:  process.env.REACT_APP_AD_PROVIDER })
                          }
                          disabled={loading}
                        >
                          <i className="mdi mdi-microsoft me-1" />
                          {t("authentication:signInGloria")}
                        </Button>

                        <ul className="list-inline"></ul>
                      </div>

                      <div className="mt-4 text-center">
                        <Link to="/forgot-password" className="text-muted">
                          <i className="mdi mdi-lock me-1" />
                          {t("authentication:forgot")}
                        </Link>
                      </div>
                    </Form>
                  </div>
                </CardBody>
                <CardFooter>
                  <div className="  text-center">
                    <p>
                      {t("authentication:questionAccount")}
                      <Link to="/register" className="fw-medium text-primary">
                        {" "}
                        {t("authentication:signup")}
                      </Link>{" "}
                    </p>
                    <Footer />
                  </div>
                </CardFooter>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
